import type { HTMLAttributes, RefObject } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';
import type { WithSX } from '@elfsight/lib-utils';
import { applySX } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import type { ShortenerClickHandlerFn } from './TextControl';
import { Control } from './TextControl';

export interface ShortenedTextProps extends HTMLAttributes<HTMLDivElement> {
  text: string;
  textDisplayMode?: 'short' | 'full';
  align?: CSSProperties['textAlign'];
  textMaxLines?: number;
  textLineHeight?: string;
  textSize?: number;
  textColor?: string;
  controlOpenText?: string;
  controlCloseText?: string;
  controlSize?: number;
  controlColor?: string;
  controlTopMargin?: number;
  withControlUnderline?: boolean;
  /**
   * displayControl нужен для скрытия/отображения контрола вне зависимости от объема текста
   */
  displayControl?: boolean;
  onShortenerClick?: ShortenerClickHandlerFn;
  onIsClampedChange?: (isClamped: boolean) => void;
  onIsShortenedChange?: (isShortened: boolean) => void;
}

export type ShortenedTextAPI = {
  toggleLines: () => void;
  expandText: () => void;
  shortenText: () => void;
  ref: RefObject<HTMLDivElement | null>;
  controlRef: RefObject<HTMLDivElement | null>;
};

export const ShortenedText = forwardRef<ShortenedTextAPI, ShortenedTextProps>(
  (
    {
      text,
      align = 'left',
      textDisplayMode = 'short',
      textMaxLines = 3,
      textSize = 14,
      textLineHeight = '',
      textColor = '#111',
      controlOpenText = 'Read more',
      controlCloseText = 'Hide',
      controlSize = 14,
      controlColor = '#999',
      controlTopMargin = 0,
      withControlUnderline = true,
      displayControl,
      onShortenerClick,
      onIsClampedChange,
      onIsShortenedChange,
      className
    },
    forwardingAPIRef
  ) => {
    const isShortMode = textDisplayMode === 'short';
    const [isShortened, setIsShortened] = useState(isShortMode);
    const [isClamped, setIsClamped] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);
    const shortenerRef = useRef<HTMLDivElement>(null);
    const controlRef = useRef<HTMLDivElement>(null);

    const lineSize = parseInt(textLineHeight) || Math.ceil(textSize * 1.4);
    const lineClamp = isShortMode && isShortened ? `${textMaxLines}` : 'unset';

    const forceShowOpenControl = isShortMode && isShortened && !!displayControl;
    const withControl =
      (isClamped || forceShowOpenControl) && displayControl !== false;

    const toggleLines = useCallback(() => {
      setIsShortened((prevState) => {
        const newState = !prevState;
        onIsShortenedChange?.(newState);

        return newState;
      });
    }, [onIsShortenedChange]);

    const controlProps = useMemo(
      () => ({
        controlSize,
        controlColor,
        toggleLines,
        onShortenerClick,
        isShortened,
        controlTopMargin,
        withControlUnderline
      }),
      [
        controlSize,
        toggleLines,
        isShortened,
        controlColor,
        onShortenerClick,
        controlTopMargin,
        withControlUnderline
      ]
    );

    useEffect(() => {
      if (!textRef.current) return;

      const checkOverflow = () => {
        const wasAlreadyExpanded = isClamped && !isShortened;
        if (wasAlreadyExpanded || !textRef.current) return;

        const tolerance = lineSize * 0.15; // 15% от lineSize

        const isOverflowing =
          textRef.current.scrollHeight - textRef.current.clientHeight >
          tolerance;

        setIsClamped(isOverflowing);
        onIsClampedChange?.(isOverflowing);
      };

      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(textRef.current);

      let requestAnimationFrameId: number | undefined;
      document.fonts?.ready.then(() => {
        requestAnimationFrameId = requestAnimationFrame(checkOverflow);
      });

      return () => {
        if (requestAnimationFrameId) {
          cancelAnimationFrame(requestAnimationFrameId);
        }
        resizeObserver.disconnect();
      };
    }, [isShortened, isClamped, lineSize, onIsClampedChange]);

    useImperativeHandle(
      forwardingAPIRef,
      () => ({
        toggleLines,
        controlRef,
        expandText: () => {
          setIsShortened(false);
          onIsShortenedChange?.(false);
          controlRef.current?.blur();
        },
        shortenText: () => {
          setIsShortened(true);
          onIsShortenedChange?.(true);
          controlRef.current?.blur();
        },
        ref: shortenerRef
      }),
      [toggleLines, onIsShortenedChange]
    );

    return (
      <Container
        className={className}
        ref={shortenerRef}
        $sx={{
          textAlign: align,
          fontSize: textSize,
          color: textColor,
          lineHeight: `${lineSize}px`
        }}
      >
        <ShortenedTextComponent
          ref={textRef}
          dangerouslySetInnerHTML={{ __html: text }}
          $sx={{
            lineClamp,
            WebkitLineClamp: lineClamp,
            lineHeight: `${lineSize}px`
          }}
        />
        {withControl && (
          <Control {...controlProps} ref={controlRef}>
            {isShortened ? controlOpenText : controlCloseText}
          </Control>
        )}
      </Container>
    );
  }
);

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'text-shortener',
    element: 'container'
  })
})<WithSX>`
  ${applySX};
  width: 100%;

  & a {
    color: inherit;
  }
`;

const ShortenedTextComponent = styled.div.attrs({
  className: createReadableClass({
    component: 'text-shortener'
  })
})<WithSX>`
  ${applySX};
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;
